import React, { useEffect, useMemo, useState } from 'react';
import Sheet from 'react-modal-sheet';

import { ErrorMessages } from '../error/errorMessages';
import { Loader, LoadingView } from './loader';
import { authSelectedPharmacist } from '../../services/apis/auth/selectedPharmacistID';
import { fetchPharmacistOfPharmacy } from '../../services/apis/dispense/pharmacistOfPharmacy';
import {
	clearInstoreDefaultPharmacist,
	saveInstoreDefaultPharmacist
} from '../../services/apis/auth/saveInstoreDefaultPharmacist';
import { clearSelectedPharmacistID } from '../../services/slices/auth/selectedPharmacist';
import { useAllSelector } from '../../services/selectors/allSelector';
import { useAuthSelector } from '../../services/selectors/authSelector';
import { findPharmacistById, getPharmacistDisplayName, idsMatch } from '../../utils/helper/helper';

import * as Icons from '../../assets/svg/icons';
import * as Hooks from '../../hooks';

export const DefaultPharmacistModal = ({
	showModal,
	setShowModal,
	onDismiss,
}) => {
	const dispatch = Hooks.useCustomDispatch();

	const { pharmacyId, saveInstoreDefaultPharmacistResponse } = useAuthSelector();
	const {
		pharmacistDetail,
		pharmacistOfPharmacyListResponse,
		driverDefaultPharmacistId,
	} = useAllSelector();

	const [selectedPharmacistId, setSelectedPharmacistId] = useState('');
	const [submitError, setSubmitError] = useState('');
	const [showPharmacistList, setShowPharmacistList] = useState(false);

	const defaultPharmacist = useMemo(
		() => findPharmacistById(pharmacistDetail, driverDefaultPharmacistId),
		[pharmacistDetail, driverDefaultPharmacistId]
	);

	const isSaving = saveInstoreDefaultPharmacistResponse?.loading;
	const hasPharmacists = (pharmacistDetail?.length || 0) > 0;

	useEffect(() => {
		if (!showModal) return;
		if (!selectedPharmacistId && defaultPharmacist?.id) {
			setSelectedPharmacistId(defaultPharmacist.id);
		}
	}, [defaultPharmacist?.id, selectedPharmacistId, showModal]);

	useEffect(() => {
		if (showModal) {
			setShowPharmacistList(false);
			setSubmitError('');
		}
	}, [showModal]);

	const handlePharmacistSelect = (id) => {
		setSelectedPharmacistId(id);
		setSubmitError('');
	};

	const handleContinue = async () => {
		if (!selectedPharmacistId) {
			setSubmitError('Please select a pharmacist to continue.');
			return;
		}

		const response = await dispatch(saveInstoreDefaultPharmacist({
			pharmacy_id: pharmacyId,
			pharmacist_id: selectedPharmacistId
		}));

		if (saveInstoreDefaultPharmacist.fulfilled.match(response)) {
			dispatch(authSelectedPharmacist(selectedPharmacistId));
			dispatch(fetchPharmacistOfPharmacy(pharmacyId));
			setShowModal(false);
			onDismiss?.();
			return;
		}

		setSubmitError('Oops! Got an issue while saving pharmacist. Please try again.');
	};

	const handleClose = () => {
		setShowModal(false);
		onDismiss?.();
	};

	const handleClearAndClose = async () => {
		const response = await dispatch(clearInstoreDefaultPharmacist({
			pharmacy_id: pharmacyId
		}));

		if (clearInstoreDefaultPharmacist.fulfilled.match(response)) {
			dispatch(clearSelectedPharmacistID());
			dispatch(fetchPharmacistOfPharmacy(pharmacyId));
			setSelectedPharmacistId('');
			setShowModal(false);
			onDismiss?.();
			return;
		}

		setSubmitError('Oops! Got an issue while clearing pharmacist. Please try again.');
	};

	const handleShowPharmacists = () => {
		setShowPharmacistList(true);
		setSubmitError('');
	};

	return (
		<Sheet isOpen={showModal} onClose={handleClose}>
			<Sheet.Container>
				<Sheet.Content>
					<div className="flex flex-col" style={{ maxHeight: '82vh' }}>
						<div className="flex items-center justify-between p-2 px-4 md:p-2 border-b border-dashed rounded-t">
							<div>
								<h3 className="text-sm font-bold text-gray-900 ml-2">Attention Needed</h3>
							</div>
							<button
								type="button"
								onClick={handleClose}
								className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center"
							>
								<Icons.CloseModelIcon />
								<span className="sr-only">Close modal</span>
							</button>
						</div>

						<Sheet.Scroller>
							<div className="p-3">
								{!showPharmacistList ? (
									<div className="">
										<p className="text-sm font-normal text-gray-900">
											Set today&rsquo;s pharmacist. Other pharmacists can still deliver when needed.
										</p>
									</div>
								) : pharmacistOfPharmacyListResponse?.loading && !hasPharmacists ? (
									<div className="flex items-center justify-center py-6">
										<LoadingView />
									</div>
								) : pharmacistOfPharmacyListResponse?.error !== null ? (
									<div className="flex items-center justify-center py-6">
										<ErrorMessages error={'Oops! Got an issue while fetching pharmacist details. Please try to reload the page again.'} />
									</div>
								) : !hasPharmacists ? (
									<div className="py-6">
										<p className="text-sm font-medium text-gray-700">No pharmacist is available right now.</p>
									</div>
								) : (
									<div className="shadow-md grid lg:grid-cols-2 md:grid-cols-2 grid-cols-1 border border-gray-100 my-2 rounded-md p-2">
										{pharmacistDetail?.map((value, index) => (
											<div key={value?.id || index} className="bg-white border border-gray-100 m-1 p-2 rounded-md">
												<label htmlFor={`default-pharmacist-modal-${index}`} className="cursor-pointer flex items-center gap-2">
													<div className="w-6 h-6 bg-gray-200 rounded-full overflow-hidden">
														<img className="w-full h-full object-cover" alt="" src={value?.cover_photo || value?.photo} />
													</div>
													<div className="flex-grow">
														<h1 className="text-sm font-medium text-gray-900">{getPharmacistDisplayName(value)}</h1>
													</div>
													<div className="flex-shrink-0">
														<input
															id={`default-pharmacist-modal-${index}`}
															type="radio"
															value={value.id}
															name="default-pharmacist-modal-selection"
															className="peer"
															onChange={() => handlePharmacistSelect(value.id)}
															checked={idsMatch(selectedPharmacistId, value.id)}
														/>
													</div>
												</label>
											</div>
										))}
									</div>
								)}
							</div>
						</Sheet.Scroller>

						<div className="pb-3 px-3 bg-white">
							{submitError &&
								<p className="mb-3 text-sm font-medium text-red-600">{submitError}</p>
							}

							{!showPharmacistList ? (
								<div className="grid grid-cols-2 gap-3 max-w-sm">
									<button
										type="button"
										onClick={isSaving ? null : handleShowPharmacists}
										disabled={isSaving}
										className="text-white w-full flex items-center justify-center focus:ring-4 focus:ring-blue-300 font-bold rounded-lg text-sm px-4 py-2.5 bg-blue-700 hover:bg-blue-800"
									>
										Yes
									</button>
									<button
										type="button"
										onClick={isSaving ? null : handleClearAndClose}
										disabled={isSaving}
										className="text-gray-700 w-full flex items-center justify-center focus:ring-4 focus:ring-gray-200 font-bold rounded-lg text-sm px-4 py-2.5 bg-gray-100 hover:bg-gray-200"
									>
										{isSaving ? <Loader /> : 'No'}
									</button>
								</div>
							) : (
								<button
									type="button"
									onClick={isSaving ? null : handleContinue}
									disabled={isSaving || !hasPharmacists}
									className={`text-white w-full flex items-center justify-center focus:ring-4 focus:ring-blue-300 font-bold rounded-lg text-lg px-5 py-3 ${selectedPharmacistId && hasPharmacists ? 'bg-blue-700 hover:bg-blue-800' : 'bg-blue-100'}`}
								>
									{isSaving ? <Loader /> : 'Continue'}
								</button>
							)}
						</div>
					</div>
				</Sheet.Content>
			</Sheet.Container>
			<Sheet.Backdrop />
		</Sheet>
	);
};

